import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { modulesService, type ModuleCode, type PlanModuleInfo, type TenantModuleStatus } from '@/services/modulesService'

type Choice = 'NONE' | 'GRANT' | 'REVOKE'

function errorMessage(e: unknown, fallback: string): string {
    const err = e as { response?: { data?: { error?: string } } }
    return err?.response?.data?.error ?? fallback
}

const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : null

/**
 * Módulos de un cliente: los de su plan más extras (con fecha de término opcional, p. ej.
 * una prueba de 30 días) o exclusiones.
 */
export default function TenantModulesPanel({ slug, name, onClose }: { slug: string; name: string; onClose: () => void }) {
    const [rows, setRows] = useState<TenantModuleStatus[]>([])
    const [catalog, setCatalog] = useState<PlanModuleInfo[]>([])
    const [draft, setDraft] = useState<Record<string, { mode: Choice; expiresAt: string; note: string }>>({})

    useEffect(() => {
        Promise.all([modulesService.tenantModules(slug), modulesService.catalog()])
            .then(([r, c]) => { setRows(r); setCatalog(c) })
            .catch(() => toast.error('No se pudieron cargar los módulos del cliente'))
    }, [slug])

    const label = (code: ModuleCode) => catalog.find((m) => m.code === code)?.label ?? code

    const current = (r: TenantModuleStatus) =>
        draft[r.module] ?? { mode: (r.override ?? 'NONE') as Choice, expiresAt: r.overrideExpiresAt?.slice(0, 10) ?? '', note: r.overrideNote ?? '' }

    const save = async (r: TenantModuleStatus) => {
        const d = current(r)
        try {
            const updated = await modulesService.setTenantModule(slug, r.module, {
                mode: d.mode, expiresAt: d.mode === 'NONE' ? undefined : d.expiresAt || undefined, note: d.note || undefined,
            })
            setRows(updated)
            setDraft(({ [r.module]: _omit, ...rest }) => rest)
            toast.success(`${label(r.module)}: actualizado`)
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo actualizar el módulo'))
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
            <div role="dialog" aria-label={`Módulos de ${name}`} onClick={(e) => e.stopPropagation()}
                className="my-8 w-full max-w-4xl rounded-xl border border-slate-700 bg-slate-900 p-6">
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold">Módulos de {name}</h2>
                        <p className="text-xs text-slate-400">
                            "Según plan" usa lo que incluye su plan. "Extra" lo agrega aunque el plan no lo traiga; "Excluido" lo quita
                            aunque el plan lo traiga. La fecha de término es opcional.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Cerrar">✕</button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-slate-400">
                            <tr>
                                <th className="px-2 py-2">Módulo</th>
                                <th className="px-2 py-2">En su plan</th>
                                <th className="px-2 py-2">Excepción</th>
                                <th className="px-2 py-2">Termina</th>
                                <th className="px-2 py-2">Nota</th>
                                <th className="px-2 py-2">Resultado</th>
                                <th className="px-2 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const d = current(r)
                                const dirty = !!draft[r.module]
                                const set = (patch: Partial<typeof d>) => setDraft((all) => ({ ...all, [r.module]: { ...d, ...patch } }))
                                return (
                                    <tr key={r.module} className="border-t border-slate-800 align-middle">
                                        <td className="px-2 py-2 text-slate-100">{label(r.module)}</td>
                                        <td className="px-2 py-2 text-slate-400">{r.inPlan ? 'Sí' : 'No'}</td>
                                        <td className="px-2 py-2">
                                            <select aria-label={`Excepción de ${label(r.module)}`} value={d.mode}
                                                onChange={(e) => set({ mode: e.target.value as Choice })}
                                                className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100">
                                                <option value="NONE">Según plan</option>
                                                <option value="GRANT">Extra</option>
                                                <option value="REVOKE">Excluido</option>
                                            </select>
                                        </td>
                                        <td className="px-2 py-2">
                                            <input type="date" disabled={d.mode === 'NONE'} value={d.expiresAt}
                                                aria-label={`Término de ${label(r.module)}`}
                                                onChange={(e) => set({ expiresAt: e.target.value })}
                                                className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100 disabled:opacity-40" />
                                        </td>
                                        <td className="px-2 py-2">
                                            <input value={d.note} disabled={d.mode === 'NONE'} maxLength={300} placeholder="p. ej. prueba 30 días"
                                                onChange={(e) => set({ note: e.target.value })}
                                                className="w-40 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100 disabled:opacity-40" />
                                        </td>
                                        <td className="px-2 py-2">
                                            <span className={`rounded-full px-2 py-0.5 text-xs ${r.effective ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
                                                {r.effective ? 'Habilitado' : 'No habilitado'}
                                            </span>
                                            {r.override && r.overrideExpiresAt && (
                                                <span className="ml-1 text-[10px] text-slate-500">hasta {fmt(r.overrideExpiresAt)}</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2 text-right">
                                            {dirty && (
                                                <button onClick={() => save(r)} className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-500">
                                                    Guardar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
